import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import DiceDreamsCheckout from '@/components/DiceDreamsCheckout';
import PaymentSettings from '@/components/PaymentSettings';
import GameCard from '@/components/GameCard';
import FootnoteContent from '@/components/FootnoteContent';
import { getMyLocation, isUserInCalifornia } from '@/services/LocationService';
import type { CheckoutFormProps, SaveCardProps, SavedCard, GameData } from '@/interfaces';
import PaymentMethodsList from '@/components/PaymentMethodsList';
import SafeTransactionInfo from '@/components/SafeTransactionInfo';
import CheckoutFormSkeleton from '@/components/skeletons/CheckoutFormSkeleton';
import LoadingOverlay from '@/components/LoadingOverlay';
import { useStyleTheme } from '@/hooks/useStyleTheme';
import { useSessionExpiration } from '@/hooks/useSessionExpiration';
import { usePaymentMethodsLoading } from '@/hooks/usePaymentMethodsLoading';
import { useResponsiveLandscape } from '@/hooks/useResponsiveLandscape';
import FailedCheckoutPage from '@/pages/FailedCheckoutPage';
import { getPayPalPaymentMethodType } from '@/lib/game-config-utils';
import Icons from '@/components/Icons';
import type { IconType } from '@/types';
import { userPaymentMethods, getPaymentMethodDefinitions } from '@/services/PaymentService';
import { isTokenExpired, getDecodedTokenFromSource } from '@/lib/token-utils';
import type { AxiosError } from 'axios';
import { validateImageUrl, formatCurrency } from '@/lib/utils';
import { getTextSize } from './hooks/useGetTextSize';
import { buildRedirectUrl } from '@/lib/redirect-utils';
import { openCustomerPortal } from '@/lib/customer-portal-utils';
import type { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import { processPayment } from '@/services/PaymentService';
import WrappedPayPalButton from '@/components/paymentMethods/WrappedPayPalButton';
import GooglePayOnly from '@/components/paymentMethods/GooglePayOnly';
import type { PaymentMethodDefinitionsResponse } from '@/interfaces';
import { isBackgroundEnabled } from '@/lib/game-config-utils';
import { useAnalytics } from '@/hooks/useAnalytics';
import {
  extractPriceProperties,
  safeTrack,
  storeCheckoutAnalyticsData,
  getCheckoutAnalyticsData,
  normalizePaymentMethod,
  sendAnalyticsEvent,
  extractSuperPropertiesFromUrl,
} from '@/services/AnalyticsService';

// Move outside component to avoid recreation on every render
const convertToSaveCardProps = (savedCard: SavedCard): SaveCardProps => {
  // Handle PayPal vaulted methods differently
  if (savedCard.credit_card_type === 'paypal') {
    return {
      id: savedCard.id,
      icon: <Icons type="card" size={46} />,
      card: {
        brand: 'PayPal',
        last4: savedCard.display_name.replace('PayPal - ', ''),
        wallet: null,
      },
      display_name: savedCard.display_name, // Keep the full display name for PayPal
      selected: false,
      onSelect: () => {},
      disabled: false,
    };
  }
  
  // Handle regular credit cards
  return {
    id: savedCard.id,
    icon: <Icons type={(savedCard.credit_card_type as IconType) || ('card' as IconType)} size={46} />,
    card: {
      brand: savedCard.credit_card_type,
      last4: savedCard.display_name.split('-').pop()?.trim() || '',
      wallet: null,
    },
    selected: false,
    onSelect: () => {},
    disabled: false,
  };
};

const CheckoutForm = ({ clientSecret, gameData, sessionData }: Omit<CheckoutFormProps, 'savedCards'>) => {
  const stripe = useStripe();
  const elements = useElements();
  const [searchParams] = useSearchParams();
  const theme = useStyleTheme(gameData);
  const { track, isEnabled } = useAnalytics();
  const hasTrackedLoadingRef = useRef(false);
  const hasTrackedImpressionRef = useRef(false);
  const hasTrackedNativePopupRef = useRef<Set<string>>(new Set());
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const { showApplePay, showGooglePay, isInitialized } = usePaymentMethodsLoading({
    stripe,
    amount: searchParams.get('amount') || '0',
  });

  const [savedCards, setSavedCardsState] = useState<SavedCard[]>([]);
  
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [showNav, setShowNav] = useState<boolean>(true);
  const [activeButton, setActiveButton] = useState<"Credit Card" | "One Click" | "More Options">("One Click");
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasExpressLoadedOnce, setHasExpressLoadedOnce] = useState(false);
  const [isExpressReinitializing, setIsExpressReinitializing] = useState(false);
  const [isCustomerPortalLoading, setIsCustomerPortalLoading] = useState(false);
  const [isPayPalProcessing, setIsPayPalProcessing] = useState(false);
  const [paypalProcessingStarted, setPaypalProcessingStarted] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [paymentMethodDefinitions, setPaymentMethodDefinitions] = useState<PaymentMethodDefinitionsResponse | null>(null);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [isCaliforniaUser, setIsCaliforniaUser] = useState<boolean>(false);

  // Track checkout loading started on first render
  // Uses useEffect to properly fire the side effect after commit
  useEffect(() => {
    if (!hasTrackedLoadingRef.current && gameData?.analytics_webhook_url) {
      hasTrackedLoadingRef.current = true;
      const superProps = extractSuperPropertiesFromUrl();
      sendAnalyticsEvent(
        gameData.analytics_webhook_url,
        'impression_checkout_loading_started',
        superProps,
        {}
      );
    }
  }, [gameData?.analytics_webhook_url]);
  
  // Fetch user location for California-specific disclaimers
  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        await getMyLocation();
        setIsCaliforniaUser(isUserInCalifornia());
      } catch (error) {
        console.error('Error fetching location:', error);
        setIsCaliforniaUser(false);
      }
    };
    fetchUserLocation();
  }, []);
  
  // Track responsive layout for landscape mobile
  const { isLandscapeMobile } = useResponsiveLandscape();
  
  // Track landscape orientation for background color
  const [isLandscape, setIsLandscape] = useState<boolean>(
    window.matchMedia("(orientation: landscape)").matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(orientation: landscape)");
    const handleChange = (e: MediaQueryListEvent) => {
      setIsLandscape(e.matches);
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Extract JWT from URL and store it (for PayPal returns)
  useEffect(() => {
    const jwtFromUrl = searchParams.get('jwt');
    if (jwtFromUrl && jwtFromUrl.startsWith('eyJ')) {
      sessionStorage.setItem('token', jwtFromUrl);
    }
  }, [searchParams]);

  // Detect if device is iPhone for safe area handling
  const isIPhone = useMemo(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipod/.test(userAgent) || (
      /ipad/.test(userAgent) && 'ontouchend' in document
    );
  }, []);

  const { sessionId, title, amount, tax, currency, image_url, country, optimisticGpay, flow } = useMemo(() => {
    const sessionId = searchParams.get('pi');
    const titleParam = searchParams.get('title');
    const title = titleParam && titleParam.trim() ? titleParam : gameData.default_product_title || 'Game';
    const image_url = searchParams.get('image_url');
    const amount = searchParams.get('amount') || '0';
    const tax = searchParams.get('tax') || '0';
    const total = searchParams.get('total') || '0';
    const flow = searchParams.get('flow');
    const currency = searchParams.get('currency') || 'USD';
    const country = searchParams.get('country') || 'US';

    // Optimistic Google Pay configuration hierarchy:
    // 1. URL param: optimistic_gpay=true/false (highest priority - overrides config)
    // 2. game_config: optimistic_gpay: true
    // 3. default: false
    const urlOptimisticGpayParam = searchParams.get('optimistic_gpay');
    const configOptimisticGpay = (gameData as any).optimistic_gpay === true;
    const optimisticGpay = urlOptimisticGpayParam !== null 
      ? urlOptimisticGpayParam === 'true'  // URL param exists, use its value
      : configOptimisticGpay;  // No URL param, use config or default to false

    return { sessionId, title, amount, tax, total, currency, image_url, country, optimisticGpay, flow };
  }, [searchParams, gameData]);
  
  useEffect(() => {
    if (flow === 'wrapped_paypal_vaulted') {
      const hasFlag = sessionStorage.getItem('wrapped_paypal_triggered');
      if (hasFlag) {
        sessionStorage.removeItem('wrapped_paypal_triggered');
        sessionStorage.removeItem('wrapped_paypal_trigger_time');
      }
    }
  }, [flow]);

  const handlePayPalReturn = useCallback(async (paypalToken: string, payerId: string) => {
    // Check if a redirect is already in progress
    const redirectInProgress = sessionStorage.getItem('paypal_redirect_in_progress');
    if (redirectInProgress === 'true') {
      return;
    }
    
    // Prevent multiple executions
    if (paypalProcessingStarted) {
      return;
    }
    
    setPaypalProcessingStarted(true);
    
    try {
      
      // Ensure we have a valid session ID
      if (!sessionId) {
        setIsPayPalProcessing(false);
        setPaypalProcessingStarted(false);
        return;
      }
      
      // Create success and cancellation redirect URLs
      const currentUrl = new URL(window.location.href);
      // Remove PayPal parameters from the URL
      currentUrl.searchParams.delete('paypal_token');
      currentUrl.searchParams.delete('PayerID');
      
      // Create success URL that goes directly to /return to avoid extra redirects
      const returnUrl = new URL(`${currentUrl.origin}/return`);
      returnUrl.searchParams.set('pi', sessionId);
      returnUrl.searchParams.set('success', 'true');
      returnUrl.searchParams.set('payment_method', 'paypal');
      
      // Get native return URL from JWT token instead of session metadata
      const decodedToken = getDecodedTokenFromSource('storage');
      const nativeReturnUrl = decodedToken?.nativeReturnUrl;
      if (nativeReturnUrl) {
        returnUrl.searchParams.set('return_url', nativeReturnUrl);
      }
      
      const successUrl = returnUrl.toString();
      const cancelUrl = `${currentUrl.toString()}${currentUrl.search ? '&' : '?'}canceled=true&payment_method=paypal`;

      // Check again if redirect is in progress before making API call
      const redirectStillInProgress = sessionStorage.getItem('paypal_redirect_in_progress');
      if (redirectStillInProgress === 'true') {
        return;
      }

      // Set the redirect flag BEFORE making the API call to prevent duplicate requests
      sessionStorage.setItem('paypal_redirect_in_progress', 'true');

      // Determine if this is a vaulted PayPal return based on game config
      const paymentMethodType = getPayPalPaymentMethodType(gameData.game_id);
      const isVaulted = paymentMethodType === 'vaulted';
      

      // Complete the PayPal payment using the charge endpoint
      const { processPayment } = await import('@/services/PaymentService');
      const chargeRequest = {
        session_id: sessionId,
        save_payment_method: isVaulted,
        is_new_payment_method: true,
        payment_method: {
          data: {
            provider: { provider: 'custom', config: isVaulted ? { 'paypal-type': 'vaulted' } : { 'paypal-type': 'one-buy' } },
            paypal_type: isVaulted ? 'paypal-vaulted' : 'paypal',
            paypal_token: paypalToken,
            payer_id: payerId
          }
        },
        success_redirect_url: successUrl,
        cancellation_redirect_url: cancelUrl
      };

      const response = await processPayment(chargeRequest);

        if (response.result.is_success) {
          try {
            // Handle success - redirect or show success message
            if (response.next_actions.length > 0) {
              for (let i = 0; i < response.next_actions.length; i++) {
                const action = response.next_actions[i];
                
                if (action.action === 'open_url' && action.parameters && action.parameters.url) {
                  // Use setTimeout to ensure redirect happens and prevent further execution
                  // setTimeout(() => {
                    window.location.href = action.parameters.url as string;
                  // }, 100);
                  
                  // Immediately return to prevent any further code execution
                  return;
                }
              }
            }
            
            // If no redirect action, go to success page directly
            window.location.href = successUrl;
          } catch (nextActionsError) {
            throw nextActionsError; // Re-throw to hit main catch block
          }
      } else {
        // Redirect to failed page
        const failedUrl = new URL(`${window.location.origin}/return`);
        failedUrl.searchParams.set('pi', sessionId);
        failedUrl.searchParams.set('error', 'payment_failed');
        failedUrl.searchParams.set('payment_method', 'paypal');
        
        window.location.href = failedUrl.toString();
      }
      } catch (error) {
        setPaypalProcessingStarted(false);
        setIsPayPalProcessing(false);
        sessionStorage.removeItem('paypal_redirect_in_progress');
        
        if (sessionId) {
          const returnUrl = new URL(`${window.location.origin}/return`);
          returnUrl.searchParams.set('pi', sessionId);
          returnUrl.searchParams.set('error', 'processing_error');
          returnUrl.searchParams.set('payment_method', 'paypal');
          window.location.href = returnUrl.toString();
        }
      }
  }, [sessionId, paypalProcessingStarted]);

  // Handle PayPal redirect responses
  useEffect(() => {
    const paypalToken = searchParams.get('paypal_token');
    const payerId = searchParams.get('PayerID');

    if (paypalToken && payerId && sessionId) {
      // Set PayPal processing state to show loading instead of FailedCheckoutPage
      setIsPayPalProcessing(true);
      
      // Ensure we have a valid JWT token for API calls
      const currentToken = sessionStorage.getItem('token');
      
      if (!currentToken || !currentToken.startsWith('eyJ')) {
        // Try to restore from backup
        const backupToken = sessionStorage.getItem('original_jwt_token');
        
        if (backupToken && backupToken.startsWith('eyJ')) {
          sessionStorage.setItem('token', backupToken);
        }
      }
      
      // This is a PayPal return - complete the payment
      handlePayPalReturn(paypalToken, payerId);
    }
  }, [searchParams, sessionId, handlePayPalReturn]);

  useEffect(() => {
    const paypalReauthComplete = searchParams.get('paypal_reauth_complete');
    
    if (paypalReauthComplete === 'true' && sessionId) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('paypal_reauth_complete');
      window.history.replaceState({}, '', currentUrl.toString());
    }
  }, [searchParams, sessionId]);

  const fetchSavedCards = useCallback(async () => {
    setIsLoadingCards(true);
    try {
      const response = await userPaymentMethods();
      if (response) {
        setSavedCardsState(response);
      } else {
        setSavedCardsState([]);
      }
    } catch (error) {
      if ((error as AxiosError).response?.status === 401) {
        return null;
      } else {
        setSavedCardsState([]);
      }
    } finally {
      setIsLoadingCards(false);
    }
  }, []);

  useEffect(() => {
    if (!sessionId) return;

    const currentToken = sessionStorage.getItem('token');
    if (!currentToken || currentToken.trim() === '') {
      return;
    }

    fetchSavedCards();
  }, [sessionId, fetchSavedCards]);

  // YOUR PAYPAL LOGIC: Handle wrapped PayPal vaulted flow for native apps
  useEffect(() => {
    if (flow !== 'wrapped_paypal_vaulted' || !sessionId) {
      return;
    }
    
    // CRITICAL: Wait for sessionData to load before proceeding
    if (!sessionData) {
      return; // Don't proceed until sessionData is available
    }
    
    const hasTriggered = sessionStorage.getItem('wrapped_paypal_triggered');
    const triggerTimestamp = sessionStorage.getItem('wrapped_paypal_trigger_time');
    const now = Date.now();
    
    if (hasTriggered === 'true' && triggerTimestamp) {
      const timeSinceTrigger = now - parseInt(triggerTimestamp);
      if (timeSinceTrigger > 30000) {
        sessionStorage.removeItem('wrapped_paypal_triggered');
        sessionStorage.removeItem('wrapped_paypal_trigger_time');
      } else {
        return;
      }
    } else if (hasTriggered === 'true') {
      sessionStorage.removeItem('wrapped_paypal_triggered');
    }
    
    sessionStorage.setItem('wrapped_paypal_triggered', 'true');
    sessionStorage.setItem('wrapped_paypal_trigger_time', Date.now().toString());

    const initiateWrappedPayPal = async () => {
      try {
        const currentToken = sessionStorage.getItem('token');
        
        if (currentToken && currentToken.startsWith('eyJ')) {
          sessionStorage.setItem('original_jwt_token', currentToken);
        }

        const amount = sessionData.price_data.total_price; // Backend already returns price in major units (dollars)
        const currency = sessionData.order.currency.toUpperCase();
        
        // Get native return URL from JWT token instead of session metadata
        const decodedToken = getDecodedTokenFromSource('storage');
        const nativeReturnUrl = decodedToken?.nativeReturnUrl;
        
        const currentUrl = new URL(window.location.href);
        const returnUrl = new URL(`${currentUrl.origin}/return`);
        returnUrl.searchParams.set('pi', sessionId);
        returnUrl.searchParams.set('paypal_status', 'vaulted_success');
        if (currentToken) {
          returnUrl.searchParams.set('jwt', currentToken);
        }
        // Include native return URL if found in JWT
        if (nativeReturnUrl) {
          returnUrl.searchParams.set('return_url', nativeReturnUrl);
        }
        
        const cancelUrl = new URL(returnUrl);
        cancelUrl.searchParams.set('paypal_status', 'vaulted_cancelled');

        const chargeRequest = {
          session_id: sessionId,
          save_payment_method: true,
          is_new_payment_method: true,
          payment_method: {
            data: {
              provider: {
                provider: 'custom',
                config: {
                  'paypal-type': 'vaulted'
                }
              },
              amount,
              currency,
              description: 'Game Purchase'
            }
          },
          success_redirect_url: returnUrl.toString(),
          cancellation_redirect_url: cancelUrl.toString()
        };

        const response = await processPayment(chargeRequest);

        if (response.next_actions?.length > 0) {
          for (const action of response.next_actions) {
            if (action.action === 'open_url' && action.parameters?.url) {
              // Clear cache to ensure fresh metadata is fetched later
              const { apiCache, createCacheKey } = await import('@/lib/api-cache');
              const cacheKey = createCacheKey('/checkout-session', { sessionId });
              apiCache.delete(cacheKey);
              
              window.location.href = action.parameters.url as string;
              return;
            }
          }
        }

        throw new Error('No redirect URL received from server');
      } catch (error) {
        sessionStorage.removeItem('wrapped_paypal_triggered');
        alert('Failed to initiate PayPal flow. Please try again.');
      }
    };

    initiateWrappedPayPal();
  }, [flow, sessionId, sessionData]); // Re-run when sessionData becomes available

  // STAGING LOGIC: Fetch payment method definitions when session data is available
  useEffect(() => {
    if (!sessionData) return;

    const fetchPaymentMethods = async () => {
      setIsLoadingPaymentMethods(true);
      try {
        const currency = sessionData.order?.currency;
        // Use country from URL query parameter if provided by the server
        const definitions = await getPaymentMethodDefinitions(currency, country);
        setPaymentMethodDefinitions(definitions);
      } catch (error) {
        console.error('Error fetching payment method definitions:', error);
        setPaymentMethodDefinitions({ definitions: [] });
      } finally {
        setIsLoadingPaymentMethods(false);
      }
    };

    fetchPaymentMethods();
  }, [sessionData, country]);

  useSessionExpiration({
    sessionData: null,
    isSessionExpired,
    setIsSessionExpired,
  });

  const displayTitle = title;

  // Get currency from session data, fallback to query param, then USD
  const displayCurrency = sessionData?.order?.currency || currency || 'USD';

  const displayAmount = useMemo(() => {
    // Use nullish check so that 0 is treated as a valid value
    if (sessionData?.price_data?.subtotal_price != null) {
      return formatCurrency(sessionData.price_data.subtotal_price, displayCurrency);
    }
    return formatCurrency(Number(amount) || 0, displayCurrency);
  }, [sessionData, amount, displayCurrency]);

  const displayTax = useMemo(() => {
    // Use nullish checks so that 0 is treated as a valid value
    if (sessionData?.price_data?.subtotal_price != null && sessionData?.price_data?.total_price != null) {
      const taxAmount = sessionData.price_data.total_price - sessionData.price_data.subtotal_price;
      return formatCurrency(taxAmount, displayCurrency);
    }
    return formatCurrency(Number(tax) || 0, displayCurrency);
  }, [sessionData, tax, displayCurrency]);

  const displayTotal = useMemo(() => {
    // Use nullish check so that 0 is treated as a valid value
    if (sessionData?.price_data?.total_price != null) {
      return formatCurrency(sessionData.price_data.total_price, displayCurrency);
    }
    return formatCurrency(Number(amount) || 0, displayCurrency);
  }, [sessionData, amount, displayCurrency]);

  const [displayImg, setDisplayImg] = useState<string>('');

  useEffect(() => {
    const determineImage = async () => {
      const queryImageUrl = image_url;

      // First, try query param image if provided, not empty, and validate it
      if (queryImageUrl && queryImageUrl.trim() && (await validateImageUrl(queryImageUrl))) {
        setDisplayImg(queryImageUrl);
        setIsImageLoaded(true);
        return;
      }

      // Then try theme default_product_image_url
      const themeImageUrl = gameData.themes?.default?.default_product_image_url;
      if (themeImageUrl) {
        setDisplayImg(themeImageUrl);
        setIsImageLoaded(true);
        return;
      }

      // Then try game config default_product_image_url
      if (gameData.default_product_image_url) {
        setDisplayImg(gameData.default_product_image_url);
        setIsImageLoaded(true);
        return;
      }

      // Finally, existing fallback
      if (gameData.product_default_image) {
        setDisplayImg(gameData.product_default_image);
        setIsImageLoaded(true);
        return;
      }

      setDisplayImg('');
      setIsImageLoaded(true); // No image is also considered "loaded"
    };

    determineImage();
  }, [image_url, gameData]);

  useEffect(() => {
    if (!displayImg) return;

    let favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;

    if (favicon) {
      favicon.href = displayImg;
    } else {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.href = displayImg;
      document.head.appendChild(favicon);
    }
  }, [displayImg]);

  const trackNativePopup = useCallback(
    (paymentMethod?: string) => {
      if (!isEnabled) return;
      const normalizedMethod =
        paymentMethod === 'applePay'
          ? 'apple_pay'
          : paymentMethod === 'googlePay'
          ? 'google_pay'
          : paymentMethod;
      if (normalizedMethod !== 'apple_pay' && normalizedMethod !== 'google_pay') {
        return;
      }
      if (hasTrackedNativePopupRef.current.has(normalizedMethod)) {
        return;
      }
      hasTrackedNativePopupRef.current.add(normalizedMethod);
      const priceProps = extractPriceProperties(sessionData);
      safeTrack(track, 'impression_purchase_native_popup', {
        payment_method: normalizedMethod,
        ...priceProps,
      });
    },
    [isEnabled, sessionData, track]
  );

  const onConfirm = useCallback(async (event: StripeExpressCheckoutElementConfirmEvent | unknown) => {
    if (!stripe || !elements) return;

    // Check if this is a PayPal payment by examining the event or current state
    // PayPal payments should be handled by SavedPayPalButton, not by Stripe's confirmPayment
    const eventObj = event as any;
    if (eventObj?.paymentMethod?.type === 'paypal' || 
        eventObj?.expressPaymentType === 'paypal' ||
        window.location.search.includes('paypal')) {
      return;
    }

    const expressPaymentType =
      eventObj?.expressPaymentType ||
      eventObj?.paymentMethod?.type ||
      eventObj?.paymentMethodType;
    trackNativePopup(expressPaymentType);

    const normalized = normalizePaymentMethod(expressPaymentType || 'card');
    storeCheckoutAnalyticsData({ ...getCheckoutAnalyticsData(), payment_method: normalized });

    const { error: submitError } = await elements.submit();
    if (submitError) {
      window.location.href = buildRedirectUrl('error', 'Submission failed');
      return;
    }

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: buildRedirectUrl('success'),
          // Only assign receipt_email if not explicitly disabled in game config (defaults to true)
          ...(gameData.assign_receipt_email !== false && {
            receipt_email: (event as StripeExpressCheckoutElementConfirmEvent)?.billingDetails?.email,
          }),
        },
        redirect: 'if_required',
      });

      if (error) {
        // Check if this is a PayPal-related error and skip it
        if (error.message?.includes('paypal_vaulted_') || error.param === 'payment_method') {
          return;
        }

        if (error.code === 'authentication_required' || error.code === 'card_declined') {
          window.location.href = buildRedirectUrl('error', error.message || 'Authentication failed');
          return;
        }

        window.location.href = buildRedirectUrl('error', error.message || 'Payment failed');
      } else if (paymentIntent) {
        window.location.href = buildRedirectUrl('success');
      }
    } catch (error: any) {
      if (error?.message?.includes('paypal_vaulted_') || error?.error?.param === 'payment_method') {
        return;
      }
      
      window.location.href = buildRedirectUrl('error', 'An unexpected error occurred');
    }
  }, [stripe, elements, clientSecret, gameData.assign_receipt_email]);

  const handleViewSavedCardsClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    await openCustomerPortal({
      gameData,
      onLoadingStart: () => setIsCustomerPortalLoading(true),
    });
  };

  const convertedSavedCards = useMemo(() => savedCards.map(convertToSaveCardProps), [savedCards]);

  // Check if PayPal is actually available in payment method definitions
  const isPayPalAvailable = useMemo(() => {
    // First check if PayPal is enabled in game config
    const configEnabled = gameData.paypal_enabled ?? true;
    
    // If not enabled in config, return false immediately
    if (!configEnabled) return false;
    
    // If enabled in config, check if PayPal is in the payment method definitions
    if (!paymentMethodDefinitions || !paymentMethodDefinitions.definitions) {
      return false;
    }
    
    // Check if any payment method definition includes PayPal
    return paymentMethodDefinitions.definitions.some(
      (definition) => definition.type?.toLowerCase() === 'paypal'
    );
  }, [gameData.paypal_enabled, paymentMethodDefinitions]);

  // Determine if all components are ready to show
  const isEverythingReady = useMemo(() => {
    return (
      !isLoadingCards && // Saved cards loaded
      isInitialized && // Payment methods initialized 
      isImageLoaded && // Image loaded or determined to not exist
      !isLoadingPaymentMethods // Payment method definitions loaded
    );
  }, [isLoadingCards, isInitialized, isImageLoaded, isLoadingPaymentMethods]);

  // Track checkout impression when everything is ready
  useEffect(() => {
    if (
      !isEnabled ||
      !isEverythingReady ||
      hasTrackedImpressionRef.current ||
      !sessionData
    ) {
      return;
    }
    hasTrackedImpressionRef.current = true;
    const priceProps = extractPriceProperties(sessionData);
    track('impression_checkout', {
      ...priceProps,
    });
  }, [isEnabled, isEverythingReady, sessionData, track]);

  // Store analytics data for success/failure pages when session data is available
  useEffect(() => {
    if (sessionData) {
      const priceProps = extractPriceProperties(sessionData);
      storeCheckoutAnalyticsData({ ...getCheckoutAnalyticsData(), ...priceProps });
    }
  }, [sessionData]);

  // PRIORITY CHECK: Show PayPal processing loading screen BEFORE any other validation
  // Check for PayPal tokens directly to avoid flash of error pages
  const paypalToken = searchParams.get('paypal_token');
  const payerId = searchParams.get('PayerID');
  
  if ((paypalToken && payerId && sessionId) || isPayPalProcessing) {
    return (
      <div className="flex justify-center items-center h-[85vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-medium">Processing PayPal payment...</p>
          <p className="text-sm text-gray-600 mt-2">Please wait while we complete your transaction</p>
        </div>
      </div>
    );
  }

  // Check if token is expired
  if (isTokenExpired('storage')) {
    return <FailedCheckoutPage />;
  }

  if (
    isSessionExpired ||
    sessionData?.status === 'canceled' ||
    sessionData?.status === 'cancelled' ||
    sessionData?.status === 'completed' ||
    sessionData?.status === 'succeeded' ||
    sessionData?.status === 'expired' ||
    (sessionData?.expires_at && Math.floor(Date.now() / 1000) >= new Date(sessionData.expires_at).getTime() / 1000)
  ) {
    return <FailedCheckoutPage />;
  }

  const textSize = getTextSize(gameData as GameData);
  
  // Show skeleton loading until everything is ready
  if (!isEverythingReady) {
    return <CheckoutFormSkeleton />;
  }

  // Handle wrapped PayPal vaulted flow (minimal UI for native app redirects)
  if (flow === 'wrapped_paypal_vaulted') {
    return (
      <div 
        className="flex items-center justify-center min-h-screen bg-white" 
        style={{ 
          backgroundColor: '#ffffff', 
          backgroundImage: 'none',
          minHeight: '100vh',
          width: '100%'
        }}
      >
        <div className="text-center p-8">
          <div className="mb-6">
            <svg className="animate-spin h-12 w-12 mx-auto text-[#0070ba]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Redirecting to PayPal...
          </h2>
          <p className="text-gray-600">
            Please wait while we redirect you to complete your payment.
          </p>
        </div>
      </div>
    );
  }

  // Handle Google Pay browser flow (dedicated Google Pay only UI for native apps)
  if (flow === 'googlepaybrowser') {
    return (
      <div 
        className="flex flex-col min-h-screen bg-white" 
        style={{ 
          backgroundColor: '#ffffff', 
          backgroundImage: 'none',
          minHeight: '100vh',
          width: '100%'
        }}
      >
        {/* Header with product info */}
        <div className="flex-shrink-0 p-4 border-b border-gray-100">
          <div className="flex items-center gap-3 max-w-md mx-auto">
            {displayImg && (
              <img 
                src={displayImg} 
                alt={displayTitle} 
                className="w-12 h-12 rounded-lg object-cover"
              />
            )}
            <div className="flex-1">
              <h1 className="text-sm font-medium text-gray-900">{displayTitle}</h1>
              <p className="text-lg font-semibold text-gray-900">{displayTotal}</p>
            </div>
          </div>
        </div>

        {/* Google Pay component */}
        <div className="flex-1 flex items-center justify-center">
          <GooglePayOnly 
            onConfirm={onConfirm}
            sessionId={sessionId || ''}
          />
        </div>

        {/* Footer with security info */}
        <div className="flex-shrink-0 p-4 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">
            Secured by Stripe. Your payment information is encrypted.
          </p>
        </div>
      </div>
    );
  }

  // Determine layout classes based on responsive state
  // Get left side background color from theme config (when bg is disabled)
  const leftSideBg = gameData.themes?.default?.landscape_left_side_bg || '#F7F7F7';
  
  // Check if background is disabled - only apply full-height styles when bg=disabled
  const bgQueryParam = searchParams.get('bg');
  const isBgDisabled = !isBackgroundEnabled(bgQueryParam, gameData);
  
  const containerClasses = `flex flex-1 flex-col landscape:flex-row relative landscape:overflow-hidden ${isBgDisabled ? 'min-h-full' : ''} ${
    isLandscapeMobile 
      ? `landscape:min-h-0 ${isBgDisabled ? 'landscape:h-full landscape:max-h-full' : ''}` 
      : ''
  }`;

  // Left panel classes (no horizontal safe area - handled by container)
  const leftPanelClasses = `landscape:w-1/2 flex flex-col landscape:overflow-y-hidden ${isBgDisabled ? 'landscape:h-full' : ''} ${
    isLandscapeMobile
      ? 'landscape:min-h-0 landscape:max-h-full'
      : ''
  }`;
  
  // Only apply background color in landscape mode
  const leftPanelStyles: React.CSSProperties = isLandscape 
    ? { backgroundColor: leftSideBg }
    : {};
  
  // Right panel should be independently scrollable (no horizontal safe area - handled by container)
  const rightPanelClasses = `flex flex-1 w-full landscape:w-1/2 flex-col pb-[17px] landscape:pt-[17px] landscape:pb-4 landscape:bg-white landscape:overflow-y-auto ${isBgDisabled ? 'landscape:h-full' : ''} justify-between ${
    isLandscapeMobile
      ? 'landscape:min-h-0 landscape:max-h-full'
      : ''
  }`;

  return (
    <>
      <LoadingOverlay 
        isVisible={isCustomerPortalLoading} 
        message="Redirecting to payment settings..." 
        isAbsolute={false}
        theme={theme}
      />
      <div className={`${containerClasses} ${isBgDisabled && isIPhone ? 'ios-safe-area' : ''}`}>
      <div className={leftPanelClasses} style={leftPanelStyles}>
        <div className="flex flex-col w-full mx-auto landscape:mb-0 landscape:h-full landscape:justify-between landscape:overflow-hidden">
          {/* Top Section - GameCard and DiceDreamsCheckout */}
          <div className="flex-shrink-0 p-2 landscape:p-1">
            <DiceDreamsCheckout 
              gameData={gameData} 
              savedCards={convertedSavedCards}
              showSettings={showSettings}
              onShowSettings={() => setShowSettings(true)}
              onHideSettings={() => setShowSettings(false)}
              onRefreshCards={fetchSavedCards}
              isInSubPage={!showNav}
              onBackToMainPage={() => setShowNav(true)}
            />

            {/* Hide GameCard in portrait mode when settings are shown */}
            <div className={`${showSettings ? 'portrait:hidden' : ''}`}>
              <GameCard img={displayImg} title={displayTitle} price={displayAmount} tax={displayTax} total={displayTotal} />
            </div>
          </div>

          {/* Footer Section - Pushed to bottom with flexible gap */}
          <div className='block portrait:hidden text-center landscape:flex-shrink-0 landscape:px-2 landscape:pb-3'>
            {/* Only show "View saved cards" link if footnote_variant is NOT stash */}
            {convertedSavedCards.length > 0 && gameData.footnote_variant !== 'stash' && (
              <a
                href="#"
                onClick={handleViewSavedCardsClick}
                className="underline cursor-pointer text-center block mb-1 text-xs"
              >
                View saved cards
              </a>
            )}
            <FootnoteContent
              gameData={gameData}
              isCaliforniaUser={isCaliforniaUser}
              textClassName={`text-center mx-6 mt-1 mb-1 landscape:leading-tight landscape:mt-1 ${
                gameData.footnote_variant === 'stash' 
                  ? 'text-[10px] landscape:mx-2' 
                  : `${textSize} landscape:text-xs`
              }`}
              showPoweredBy={true}
              poweredByClassName="mt-2"
            />
          </div>
          
          <div className={`h-[1px] bg-[#ECECEC] w-full mb-4 landscape:hidden ${showSettings ? 'portrait:hidden' : ''}`} />
        </div>
      </div>

      {/* Right panel - hide in portrait when settings are shown */}
      <div className={`${rightPanelClasses} ${showSettings ? 'portrait:hidden' : ''}`}>
        
        {/* Middle Section - Header + Payment Methods */}
        <div className="flex-1 p-2 flex flex-col">
          {showSettings ? (
            // YOUR LOGIC: Show settings component in right panel for landscape mode only
            <div className="landscape:relative landscape:h-full">
              <PaymentSettings
                gameData={gameData}
                savedCards={convertedSavedCards}
                onBack={() => setShowSettings(false)}
                onRefreshCards={fetchSavedCards}
              />
            </div>
          ) : (
            // STAGING LOGIC: Show normal payment methods
            <>
              {showNav && <h3 className="mb-2 md:mb-3 landscape:mb-4 mx-5 text-[#212121] font-['Poppins'] text-[15px] font-medium leading-[normal] text-edge-cap leading-trim-both landscape:mt-[-7px]">Pick a payment method</h3>}
              <div className="flex-1 flex flex-col justify-end">
                <PaymentMethodsList
                  savedCards={convertedSavedCards}
                  email={email}
                  setEmail={setEmail}
                  emailError={emailError}
                  setEmailError={setEmailError}
                  onConfirm={onConfirm}
                  showApplePay={showApplePay}
                  showGooglePay={showGooglePay}
                  isPaymentMethodsLoading={false}
                  clientSecret={clientSecret}
                  showNav={showNav}
                  setShowNav={setShowNav}
                  activeButton={activeButton}
                  setActiveButton={setActiveButton}
                  isSkeletonPhase={!isEverythingReady}
                  hasExpressLoadedOnce={hasExpressLoadedOnce}
                  onExpressLoaded={() => setHasExpressLoadedOnce(true)}
                  isExpressReinitializing={isExpressReinitializing}
                  paypalEnabled={isPayPalAvailable}
                  onExpressReinitialized={() => {
                    if (isExpressReinitializing) {
                      setIsExpressReinitializing(false);
                    } else {
                      setIsExpressReinitializing(true);
                      // Fallback: auto-reset after 500ms in case onReady doesn't fire
                      setTimeout(() => {
                        setIsExpressReinitializing(false);
                      }, 500);
                    }
                  }}
                  paymentMethodDefinitions={paymentMethodDefinitions}
                  optimisticGpay={optimisticGpay}
                  gameData={gameData}
                  sessionId={sessionId || ''}
                />
                
                {/* YOUR LOGIC: Demo mode wrapped PayPal button */}
                {gameData.demo_mode && (
                  <div className="px-5 mt-4 pt-4 border-t border-gray-200">
                    <WrappedPayPalButton 
                      sessionId={sessionId || ''} 
                      disabled={!sessionId}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer Section - SafeTransactionInfo + Terms (portrait only) */}
        <div className="mt-auto p-2 flex flex-col justify-end">
          <SafeTransactionInfo savedCards={convertedSavedCards} gameData={gameData} activeButton={activeButton} isLoadingCards={false} />
        </div>
      </div>
      
    </div>
    </>
  );
};

export default React.memo(CheckoutForm);
